import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { MapErrorBoundary } from './MapErrorBoundary'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { CountrySelect } from './CountrySelect'
import { AppIcon } from './ui/icon'
import { mdiMagnify, mdiClose, mdiChevronDown, mdiChevronUp, mdiPencil } from '@mdi/js'

// --- Types ---

export interface Iso20022Address {
  addressType?: { code: string }
  streetName?: string
  buildingNumber?: string
  buildingName?: string
  floor?: string
  room?: string
  postBox?: string
  postCode?: string
  townName?: string
  townLocationName?: string
  districtName?: string
  countrySubDivision?: string
  country?: string
  department?: string
  subDepartment?: string
  careOf?: string
  attentionOf?: string
  registrationAddressIndicator?: boolean
  addressLine?: string[]
}

export interface AddressData {
  iso20022: Iso20022Address
  raw: string
  geolocation: { lat: number; lng: number } | null
  placeId: string | null
  buildingName: string | null
}

export interface AddressSearchProps {
  initialAddress?: Iso20022Address | Record<string, any> | null
  initialRaw?: string
  initialGeolocation?: { lat: number; lng: number } | null
  initialPlaceId?: string

  onChange: (data: AddressData) => void

  showMap?: boolean
  showDetails?: boolean
  showRawPreview?: boolean
  compact?: boolean
  autoSearch?: boolean
}

// --- Helpers ---

const ADDRESS_TYPES = [
  { code: 'ADDR', label: 'Postal' },
  { code: 'PBOX', label: 'PO Box' },
  { code: 'HOME', label: 'Residential' },
  { code: 'BIZZ', label: 'Business' },
  { code: 'MLTO', label: 'Mail To' },
  { code: 'DLVY', label: 'Delivery' },
]

export function generateRawAddress(address: Iso20022Address | Record<string, any>): string {
  if (address.addressLine?.length > 0) {
    return [...address.addressLine, address.country].filter(Boolean).join(', ')
  }
  const parts = [
    [address.buildingNumber, address.streetName].filter(Boolean).join(' '),
    address.buildingName,
    [address.postCode, address.townName].filter(Boolean).join(' '),
    address.countrySubDivision,
    address.country,
  ]
  return parts.filter(Boolean).join(', ')
}

export function mapPlaceToIso20022(place: google.maps.places.PlaceResult): Iso20022Address {
  const components = place.address_components || []
  const get = (type: string, useShort = false): string => {
    const c = components.find(c => c.types.includes(type))
    return c ? (useShort ? c.short_name : c.long_name) : ''
  }

  return {
    streetName: get('route'),
    buildingNumber: get('street_number'),
    postCode: get('postal_code'),
    townName: get('locality') || get('postal_town') || get('administrative_area_level_2'),
    countrySubDivision: get('administrative_area_level_1'),
    country: get('country', true), // ISO 3166-1 alpha-2
  }
}

// --- Inner search component (needs Map context) ---

interface PlaceSuggestion {
  place_id: string
  description: string
}

function AutocompleteSearchBox({
  onPlaceSelect,
  initialQuery,
  autoSearch,
}: {
  onPlaceSelect: (place: google.maps.places.PlaceResult, placeId: string) => void
  initialQuery?: string
  autoSearch?: boolean
}) {
  const { t } = useTranslation('properties')
  const map = useMap()
  const places = useMapsLibrary('places')
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [query, setQuery] = useState(initialQuery || '')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null)
  const placesService = useRef<google.maps.places.PlacesService | null>(null)
  const autoSearchDone = useRef(false)

  useEffect(() => {
    if (!places || !map) return
    autocompleteService.current = new places.AutocompleteService()
    placesService.current = new places.PlacesService(map)
  }, [places, map])

  // Auto-search on mount
  useEffect(() => {
    if (!autoSearch || !initialQuery || autoSearchDone.current || !autocompleteService.current) return
    autoSearchDone.current = true
    autocompleteService.current.getPlacePredictions(
      { input: initialQuery, types: ['address'] },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.map(p => ({ place_id: p.place_id, description: p.description })))
          setShowSuggestions(true)
        }
      }
    )
  }, [autoSearch, initialQuery])

  const handleInputChange = useCallback((value: string) => {
    setQuery(value)
    if (!value.trim() || !autocompleteService.current) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    autocompleteService.current.getPlacePredictions(
      { input: value, types: ['address'] },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.map(p => ({ place_id: p.place_id, description: p.description })))
          setShowSuggestions(true)
        } else {
          setSuggestions([])
          setShowSuggestions(false)
        }
      }
    )
  }, [])

  const handleSuggestionClick = useCallback((suggestion: PlaceSuggestion) => {
    if (!placesService.current) return
    placesService.current.getDetails(
      { placeId: suggestion.place_id, fields: ['geometry', 'address_components', 'name', 'formatted_address', 'place_id'] },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          setQuery(suggestion.description)
          setShowSuggestions(false)
          onPlaceSelect(place, suggestion.place_id)
          if (map && place.geometry?.location) {
            map.setCenter({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() })
            map.setZoom(16)
          }
        }
      }
    )
  }, [map, onPlaceSelect])

  const handleClear = useCallback(() => {
    setQuery('')
    setSuggestions([])
    setShowSuggestions(false)
  }, [])

  return (
    <div className="relative">
      <div className="relative">
        <AppIcon tone="muted" path={mdiMagnify} size="sm" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input
          type="text"
          placeholder={t('address_editor.search_address')}
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          className="pl-9 pr-8"
          data-testid="address-search-input"
        />
        {query && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={handleClear}
            data-testid="address-search-clear"
          >
            <AppIcon path={mdiClose} size="xs" />
          </button>
        )}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              className="w-full px-4 py-2 text-left hover:bg-accent focus:bg-accent focus:outline-none text-foreground text-sm"
              onClick={() => handleSuggestionClick(suggestion)}
              data-testid="address-suggestion"
            >
              {suggestion.description}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Main component ---

function AddressSearchInner({
  initialAddress,
  initialRaw,
  initialGeolocation,
  initialPlaceId,
  onChange,
  showMap = true,
  showDetails = true,
  showRawPreview = true,
  compact = false,
  autoSearch = false,
}: AddressSearchProps) {
  const { t } = useTranslation('properties')

  // Structured fields
  const [addressType, setAddressType] = useState('')
  const [streetName, setStreetName] = useState('')
  const [buildingNumber, setBuildingNumber] = useState('')
  const [buildingName, setBuildingName] = useState('')
  const [floor, setFloor] = useState('')
  const [room, setRoom] = useState('')
  const [postBox, setPostBox] = useState('')
  const [postCode, setPostCode] = useState('')
  const [townName, setTownName] = useState('')
  const [townLocationName, setTownLocationName] = useState('')
  const [districtName, setDistrictName] = useState('')
  const [countrySubDivision, setCountrySubDivision] = useState('')
  const [country, setCountry] = useState('')
  const [department, setDepartment] = useState('')
  const [subDepartment, setSubDepartment] = useState('')
  const [careOf, setCareOf] = useState('')
  const [attentionOf, setAttentionOf] = useState('')
  const [registrationAddress, setRegistrationAddress] = useState(false)

  // Location
  const [geolocation, setGeolocation] = useState<{ lat: number; lng: number } | null>(initialGeolocation || null)
  const [placeId, setPlaceId] = useState<string | null>(initialPlaceId || null)
  const [placeBuildingName, setPlaceBuildingName] = useState<string | null>(null)

  // Coordinates editing
  const [editingCoords, setEditingCoords] = useState(false)
  const [latInput, setLatInput] = useState('')
  const [lngInput, setLngInput] = useState('')

  // Raw address
  const [rawOverride, setRawOverride] = useState(false)
  const [rawValue, setRawValue] = useState('')

  // Details section
  const [showDetailsSection, setShowDetailsSection] = useState(false)
  const [showOptional, setShowOptional] = useState(false)

  // Initialize from props
  useEffect(() => {
    if (initialAddress && typeof initialAddress === 'object') {
      const addr = initialAddress as Record<string, any>
      setAddressType(addr.addressType?.code || '')
      setStreetName(addr.streetName || '')
      setBuildingNumber(addr.buildingNumber || '')
      setBuildingName(addr.buildingName || '')
      setFloor(addr.floor || '')
      setRoom(addr.room || '')
      setPostBox(addr.postBox || '')
      setPostCode(addr.postCode || '')
      setTownName(addr.townName || '')
      setTownLocationName(addr.townLocationName || '')
      setDistrictName(addr.districtName || '')
      setCountrySubDivision(addr.countrySubDivision || '')
      setCountry(addr.country || '')
      setDepartment(addr.department || '')
      setSubDepartment(addr.subDepartment || '')
      setCareOf(addr.careOf || '')
      setAttentionOf(addr.attentionOf || '')
      setRegistrationAddress(addr.registrationAddressIndicator || false)
      if (addr.department || addr.subDepartment || addr.careOf || addr.attentionOf || addr.registrationAddressIndicator) {
        setShowOptional(true)
      }
    }
    if (initialGeolocation) {
      setGeolocation(initialGeolocation)
    }
    if (initialPlaceId) {
      setPlaceId(initialPlaceId)
    }
    if (initialRaw && initialAddress) {
      const computed = generateRawAddress(initialAddress)
      if (initialRaw !== computed) {
        setRawOverride(true)
        setRawValue(initialRaw)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only on mount — initial* props are read once

  // Build current address object
  const buildAddress = useCallback((): Iso20022Address => {
    const result: Iso20022Address = {}
    if (addressType) result.addressType = { code: addressType }
    if (streetName) result.streetName = streetName
    if (buildingNumber) result.buildingNumber = buildingNumber
    if (buildingName) result.buildingName = buildingName
    if (floor) result.floor = floor
    if (room) result.room = room
    if (postBox) result.postBox = postBox
    if (postCode) result.postCode = postCode
    if (townName) result.townName = townName
    if (townLocationName) result.townLocationName = townLocationName
    if (districtName) result.districtName = districtName
    if (countrySubDivision) result.countrySubDivision = countrySubDivision
    if (country) result.country = country
    if (department) result.department = department
    if (subDepartment) result.subDepartment = subDepartment
    if (careOf) result.careOf = careOf
    if (attentionOf) result.attentionOf = attentionOf
    if (registrationAddress) result.registrationAddressIndicator = true
    return result
  }, [addressType, streetName, buildingNumber, buildingName, floor, room, postBox, postCode, townName, townLocationName, districtName, countrySubDivision, country, department, subDepartment, careOf, attentionOf, registrationAddress])

  const computedRaw = useMemo(() => generateRawAddress(buildAddress()), [buildAddress])

  // Notify parent on meaningful changes
  const notifyChange = useCallback((overrides?: Partial<{ geo: typeof geolocation; pid: typeof placeId; bname: typeof placeBuildingName; addr: Iso20022Address; raw: string }>) => {
    const addr = overrides?.addr ?? buildAddress()
    const raw = overrides?.raw ?? (rawOverride ? rawValue : generateRawAddress(addr))
    onChange({
      iso20022: addr,
      raw,
      geolocation: overrides?.geo !== undefined ? overrides.geo : geolocation,
      placeId: overrides?.pid !== undefined ? overrides.pid : placeId,
      buildingName: overrides?.bname !== undefined ? overrides.bname : placeBuildingName,
    })
  }, [buildAddress, rawOverride, rawValue, geolocation, placeId, placeBuildingName, onChange])

  // Place selection handler
  const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult, selectedPlaceId: string) => {
    const iso = mapPlaceToIso20022(place)
    setStreetName(iso.streetName || '')
    setBuildingNumber(iso.buildingNumber || '')
    setPostCode(iso.postCode || '')
    setTownName(iso.townName || '')
    setCountrySubDivision(iso.countrySubDivision || '')
    setCountry(iso.country || '')

    const loc = place.geometry?.location
      ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() }
      : null
    setGeolocation(loc)
    setPlaceId(selectedPlaceId)

    // Extract building name if different from street address. Google returns
    // the *abbreviated* formatted address as place.name for plain addresses
    // ("111 Bd de Magenta" vs streetName "Boulevard de Magenta"), so a strict
    // equality check lets the address leak in as a bogus building name — also
    // reject any name that starts with the building number.
    const formattedStreet = [iso.buildingNumber, iso.streetName].filter(Boolean).join(' ')
    const looksLikeAddress = !!iso.buildingNumber && !!place.name?.trim().startsWith(iso.buildingNumber)
    const bname = place.name && place.name !== formattedStreet && !looksLikeAddress ? place.name : null
    setPlaceBuildingName(bname)
    if (bname) setBuildingName(bname)

    setRawOverride(false)

    // Compute raw from new fields
    const newAddr: Iso20022Address = { ...iso }
    if (bname) newAddr.buildingName = bname
    const raw = generateRawAddress(newAddr)

    onChange({
      iso20022: newAddr,
      raw,
      geolocation: loc,
      placeId: selectedPlaceId,
      buildingName: bname,
    })
  }, [onChange])

  // Map click → reverse geocode
  const handleMapClick = useCallback((event: any) => {
    if (!event.detail.latLng) return
    const loc = { lat: event.detail.latLng.lat, lng: event.detail.latLng.lng }
    setGeolocation(loc)

    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ location: loc }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const place = results[0] as unknown as google.maps.places.PlaceResult
        const iso = mapPlaceToIso20022(place)
        setStreetName(iso.streetName || '')
        setBuildingNumber(iso.buildingNumber || '')
        setPostCode(iso.postCode || '')
        setTownName(iso.townName || '')
        setCountrySubDivision(iso.countrySubDivision || '')
        setCountry(iso.country || '')
        setRawOverride(false)

        const newAddr: Iso20022Address = { ...iso }
        onChange({
          iso20022: newAddr,
          raw: generateRawAddress(newAddr),
          geolocation: loc,
          placeId: results[0].place_id || null,
          buildingName: placeBuildingName,
        })
        setPlaceId(results[0].place_id || null)
      } else {
        notifyChange({ geo: loc })
      }
    })
  }, [onChange, placeBuildingName, notifyChange])

  // Coordinate manual edit
  const handleCoordsConfirm = useCallback(() => {
    const lat = parseFloat(latInput)
    const lng = parseFloat(lngInput)
    if (!isNaN(lat) && !isNaN(lng)) {
      const loc = { lat, lng }
      setGeolocation(loc)
      setEditingCoords(false)
      notifyChange({ geo: loc })
    }
  }, [latInput, lngInput, notifyChange])

  // Keep a ref to the latest notifyChange so deferred callbacks always
  // see fresh state (setTimeout would otherwise capture a stale closure
  // whose buildAddress() still reads the pre-edit values).
  const notifyChangeRef = useRef(notifyChange)
  useEffect(() => {
    notifyChangeRef.current = notifyChange
  })

  // Track pending field change for useEffect-based notification
  const [fieldChangeCounter, setFieldChangeCounter] = useState(0)

  // Notify parent after React re-renders with the updated state
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (fieldChangeCounter > 0) {
      notifyChangeRef.current()
    }
  }, [fieldChangeCounter])

  // Field change → trigger re-render, then notify via useEffect
  const handleFieldChange = useCallback((setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    setter(value)
    setFieldChangeCounter(c => c + 1)
  }, [])

  const mapCenter = geolocation || { lat: 48.8566, lng: 2.3522 }
  const autoSearchEnabled = autoSearch && !!initialRaw && !initialGeolocation && !initialPlaceId

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <AutocompleteSearchBox
        onPlaceSelect={handlePlaceSelect}
        initialQuery={initialRaw || ''}
        autoSearch={autoSearchEnabled}
      />

      {/* Map */}
      {showMap && (
        <div className={`w-full border rounded-lg overflow-hidden ${compact ? 'h-64' : 'h-80'}`}>
          <MapErrorBoundary>
          <Map
            defaultCenter={mapCenter}
            defaultZoom={geolocation ? 16 : 5}
            mapId="address-search-map"
            onClick={handleMapClick}
          >
            {geolocation && <AdvancedMarker position={geolocation} />}
          </Map>
          </MapErrorBoundary>
        </div>
      )}

      {/* Coordinates display/edit */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t('address_editor.latitude')}:</span>
        {editingCoords ? (
          <>
            <Input
              type="number"
              step="any"
              value={latInput}
              onChange={(e) => setLatInput(e.target.value)}
              className="w-28 h-7 text-xs"
              data-testid="address-lat-input"
            />
            <span className="text-muted-foreground">{t('address_editor.longitude')}:</span>
            <Input
              type="number"
              step="any"
              value={lngInput}
              onChange={(e) => setLngInput(e.target.value)}
              className="w-28 h-7 text-xs"
              data-testid="address-lng-input"
            />
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={handleCoordsConfirm} data-testid="address-coords-confirm">
              OK
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditingCoords(false)}>
              <AppIcon path={mdiClose} size="xs" />
            </Button>
          </>
        ) : (
          <>
            <span data-testid="address-coords-display">
              {geolocation ? `${geolocation.lat.toFixed(6)}, ${geolocation.lng.toFixed(6)}` : '-'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1"
              onClick={() => {
                setLatInput(geolocation?.lat.toFixed(6) || '')
                setLngInput(geolocation?.lng.toFixed(6) || '')
                setEditingCoords(true)
              }}
              data-testid="address-coords-edit"
            >
              <AppIcon path={mdiPencil} size="xs" />
            </Button>
          </>
        )}
      </div>

      {/* Edit address details (collapsible structured fields) */}
      {showDetails && (
        <Collapsible open={showDetailsSection} onOpenChange={setShowDetailsSection}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" data-testid="address-details-toggle">
              <AppIcon path={showDetailsSection ? mdiChevronUp : mdiChevronDown} size="sm" className="mr-1" />
              {t('address_editor.structured')}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 mt-2">
            {/* Address type */}
            <div>
              <Label>{t('address_editor.address_type')}</Label>
              <Select value={addressType} onValueChange={(v) => handleFieldChange(setAddressType, v)}>
                <SelectTrigger data-testid="address-field-addressType">
                  <SelectValue placeholder={t('address_editor.address_type')} />
                </SelectTrigger>
                <SelectContent>
                  {ADDRESS_TYPES.map(at => (
                    <SelectItem key={at.code} value={at.code}>{at.code} - {at.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Street group */}
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label>{t('address_editor.building_number')}</Label>
                <Input
                  value={buildingNumber}
                  onChange={(e) => handleFieldChange(setBuildingNumber, e.target.value.slice(0, 16))}
                  data-testid="address-field-buildingNumber"
                  maxLength={16}
                />
              </div>
              <div className="col-span-3">
                <Label>{t('address_editor.street_name')}</Label>
                <Input
                  value={streetName}
                  onChange={(e) => handleFieldChange(setStreetName, e.target.value.slice(0, 70))}
                  data-testid="address-field-streetName"
                  maxLength={70}
                />
              </div>
            </div>

            <div>
              <Label>{t('address_editor.building_name')}</Label>
              <Input
                value={buildingName}
                onChange={(e) => handleFieldChange(setBuildingName, e.target.value.slice(0, 35))}
                data-testid="address-field-buildingName"
                maxLength={35}
              />
            </div>

            {/* Floor/Room/PO Box */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>{t('address_editor.floor')}</Label>
                <Input
                  value={floor}
                  onChange={(e) => handleFieldChange(setFloor, e.target.value.slice(0, 70))}
                  data-testid="address-field-floor"
                  maxLength={70}
                />
              </div>
              <div>
                <Label>{t('address_editor.room')}</Label>
                <Input
                  value={room}
                  onChange={(e) => handleFieldChange(setRoom, e.target.value.slice(0, 70))}
                  data-testid="address-field-room"
                  maxLength={70}
                />
              </div>
              <div>
                <Label>{t('address_editor.post_box')}</Label>
                <Input
                  value={postBox}
                  onChange={(e) => handleFieldChange(setPostBox, e.target.value.slice(0, 16))}
                  data-testid="address-field-postBox"
                  maxLength={16}
                />
              </div>
            </div>

            {/* Post code / Town */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{t('address_editor.post_code')}</Label>
                <Input
                  value={postCode}
                  onChange={(e) => handleFieldChange(setPostCode, e.target.value.slice(0, 16))}
                  data-testid="address-field-postCode"
                  maxLength={16}
                />
              </div>
              <div>
                <Label>{t('address_editor.town')}</Label>
                <Input
                  value={townName}
                  onChange={(e) => handleFieldChange(setTownName, e.target.value.slice(0, 35))}
                  data-testid="address-field-townName"
                  maxLength={35}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{t('address_editor.town_location')}</Label>
                <Input
                  value={townLocationName}
                  onChange={(e) => handleFieldChange(setTownLocationName, e.target.value.slice(0, 35))}
                  data-testid="address-field-townLocationName"
                  maxLength={35}
                />
              </div>
              <div>
                <Label>{t('address_editor.district')}</Label>
                <Input
                  value={districtName}
                  onChange={(e) => handleFieldChange(setDistrictName, e.target.value.slice(0, 35))}
                  data-testid="address-field-districtName"
                  maxLength={35}
                />
              </div>
            </div>

            {/* Region / Country */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{t('address_editor.country_subdivision')}</Label>
                <Input
                  value={countrySubDivision}
                  onChange={(e) => handleFieldChange(setCountrySubDivision, e.target.value.slice(0, 35))}
                  data-testid="address-field-countrySubDivision"
                  maxLength={35}
                />
              </div>
              <div>
                <Label>{t('address_editor.country')}</Label>
                <CountrySelect
                  value={country}
                  onValueChange={(v) => handleFieldChange(setCountry, v)}
                  includeAll={false}
                  data-testid="address-country-select"
                />
              </div>
            </div>

            {/* Optional fields */}
            <Collapsible open={showOptional} onOpenChange={setShowOptional}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground">
                  <AppIcon path={showOptional ? mdiChevronUp : mdiChevronDown} size="sm" className="mr-1" />
                  {t('address_editor.department')}, {t('address_editor.care_of')}...
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>{t('address_editor.department')}</Label>
                    <Input value={department} onChange={(e) => handleFieldChange(setDepartment, e.target.value)} data-testid="address-field-department" />
                  </div>
                  <div>
                    <Label>{t('address_editor.sub_department')}</Label>
                    <Input value={subDepartment} onChange={(e) => handleFieldChange(setSubDepartment, e.target.value)} data-testid="address-field-subDepartment" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>{t('address_editor.care_of')}</Label>
                    <Input value={careOf} onChange={(e) => handleFieldChange(setCareOf, e.target.value)} data-testid="address-field-careOf" />
                  </div>
                  <div>
                    <Label>{t('address_editor.attention_of')}</Label>
                    <Input value={attentionOf} onChange={(e) => handleFieldChange(setAttentionOf, e.target.value)} data-testid="address-field-attentionOf" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={registrationAddress}
                    onChange={(e) => { setRegistrationAddress(e.target.checked); setFieldChangeCounter(c => c + 1) }}
                    id="registration-address"
                    data-testid="address-field-registrationAddressIndicator"
                  />
                  <Label htmlFor="registration-address">{t('address_editor.registration_address')}</Label>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Raw address preview */}
      {showRawPreview && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">{t('address_editor.raw_preview')}</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={() => {
                if (!rawOverride) setRawValue(computedRaw)
                setRawOverride(!rawOverride)
              }}
              data-testid="address-raw-edit-btn"
            >
              <AppIcon path={mdiPencil} size="xs" />
            </Button>
          </div>
          {rawOverride ? (
            <Input
              value={rawValue}
              onChange={(e) => { setRawValue(e.target.value); setFieldChangeCounter(c => c + 1) }}
              data-testid="address-raw-preview"
            />
          ) : (
            <p className="text-sm text-muted-foreground bg-muted/50 rounded px-2 py-1" data-testid="address-raw-preview">
              {computedRaw || '-'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// --- Wrapper with APIProvider ---

export function AddressSearch(props: AddressSearchProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

  if (!apiKey) {
    return (
      <div className="text-sm text-muted-foreground p-4 border rounded">
        Google Maps API key not configured. Set VITE_GOOGLE_MAPS_API_KEY in your environment.
      </div>
    )
  }

  return (
    <APIProvider apiKey={apiKey}>
      <AddressSearchInner {...props} />
    </APIProvider>
  )
}
